import { apiRequest } from './api'
import { FuneralCase, CreateCaseData, Booking } from '../types'

interface CasesResponse {
  data: FuneralCase[]
  total: number
  page: number
  limit: number
  totalPages: number
}

interface DashboardStats {
  totalCases: number
  activeCases: number
  completedCases: number
  pendingDocuments: number
}

export const casesService = {
  // Get all cases with pagination
  async getCases(page = 1, limit = 10): Promise<CasesResponse> {
    const response = await apiRequest.get<CasesResponse>('/cases', {
      page: page.toString(),
      limit: limit.toString(),
    })
    return response.data
  },

  // Get a single case by ID
  async getCase(id: string): Promise<FuneralCase> {
    const response = await apiRequest.get<FuneralCase>(`/cases/${id}`)
    return response.data
  },

  // Create a new case
  async createCase(caseData: CreateCaseData): Promise<FuneralCase> {
    const response = await apiRequest.post<FuneralCase>('/cases', caseData)
    return response.data
  },

  // Create a case through workflow (public endpoint)
  async createCaseWithWorkflow(workflowData: any): Promise<FuneralCase> {
    const response = await apiRequest.post<FuneralCase>('/public/create-case', workflowData)
    return response.data
  },

  // Create a case with provider matching results (authenticated endpoint)
  async createCaseWithProviders(workflowData: any): Promise<FuneralCase> {
    const response = await apiRequest.post<FuneralCase>('/cases/create-with-providers', workflowData)
    return response.data
  },

  // Book a provider for a specific case
  async bookProvider(caseId: string, bookingData: {
    providerId: string;
    serviceType: string;
    scheduledDateTime: string;
    duration?: number;
    location?: string;
    coordinates?: { lat: number; lng: number };
    specialRequests?: string;
    pricing?: any;
  }): Promise<Booking> {
    const response = await apiRequest.post<Booking>(`/cases/${caseId}/book-provider`, bookingData)
    return response.data
  },

  // Update an existing case
  async updateCase(id: string, caseData: Partial<CreateCaseData>): Promise<FuneralCase> {
    const response = await apiRequest.patch<FuneralCase>(`/cases/${id}`, caseData)
    return response.data
  },

  // Update case status
  async updateCaseStatus(id: string, status: string): Promise<FuneralCase> {
    const response = await apiRequest.patch<FuneralCase>(`/cases/${id}/status`, { status })
    return response.data
  },

  // Delete a case
  async deleteCase(id: string): Promise<{ message: string }> {
    const response = await apiRequest.delete<{ message: string }>(`/cases/${id}`)
    return response.data
  },

  // Search cases
  async searchCases(query: string): Promise<FuneralCase[]> {
    const response = await apiRequest.get<FuneralCase[]>('/cases/search', { q: query })
    return response.data
  },

  // Get dashboard statistics
  async getDashboardStats(): Promise<DashboardStats> {
    const response = await apiRequest.get<DashboardStats>('/cases/dashboard/stats')
    return response.data
  },
}